import PropTypes from 'prop-types'
import React from 'react'
import classNames from 'classnames'

import { toRoman } from 'helpers'

import {
    Checkbox,
    ScrollArea,
    Table,
    TableHead,
    TableRow,
    TableBody,
} from 'react-wot-bootstrap'

import Nation from 'components/Nation/Nation'
import VehicleFilterCheckbox from 'components/VehicleFilterCheckbox/VehicleFilterCheckbox'
import VehicleType from 'components/VehicleType/VehicleType'
import DefaultTooltip from 'components/DefaultTooltip/DefaultTooltip'

import {
    NAME_COLUMN_TITLE,
    NOT_FOUND_MESSAGE,
    VEHICLE_NOT_READY_TOOLTIP,
    HEADER_TOOLTIPS,
} from './constants'

import { VehiclePropTypes } from 'containers/BattleRooms/propTypes'

import styles from './VehiclesTable.scss'

const TABLE_ROW_HEIGHT = 34
const TABLE_HEIGHT = 264 // equals VehiclesTable.scss `.list` height

export class VehiclesTable extends React.Component {
    static propTypes = {
        isOpen: PropTypes.bool.isRequired,
        onHeaderClick: PropTypes.func.isRequired,
        onSelectedVehicleCdsUpdate: PropTypes.func.isRequired,
        realm: PropTypes.string.isRequired,
        selectedVehicleCds: PropTypes.arrayOf(PropTypes.number).isRequired,
        sortColumn: PropTypes.string.isRequired,
        sortOrder: PropTypes.string,
        vehicles: PropTypes.arrayOf(VehiclePropTypes).isRequired,
    }

    constructor(props) {
        super(props)

        this.renderTableRow = this.renderTableRow.bind(this)
    }

    componentDidUpdate(prevProps) {
        if (prevProps.isOpen !== this.props.isOpen && this._scrollArea) this._scrollArea.scrollTop(0)
    }

    handleHeaderClick(name) {
        this.props.onHeaderClick(name)
    }

    toggleVehicleSelected = vehicleCd => {
        let { selectedVehicleCds } = this.props

        if (selectedVehicleCds.includes(vehicleCd)){
            selectedVehicleCds = selectedVehicleCds.filter(id => id !== vehicleCd)
        } else {
            selectedVehicleCds = [...selectedVehicleCds, vehicleCd]
        }
        this.props.onSelectedVehicleCdsUpdate(selectedVehicleCds)
    }

    addVehicles = (source, adding) => {
        const sourceWithAdded = [...source]
        adding.forEach(vehicleCd => !source.includes(vehicleCd) && sourceWithAdded.push(vehicleCd))
        return sourceWithAdded
    }

    removeVehicles = (source, removing) => source.filter(vehicle => !removing.includes(vehicle))

    handleSelectAllClick = () => {
        const selectedVehicleCds = [...this.props.selectedVehicleCds]
        const filteredVehicleCds = this.props.vehicles.map(vehicle => vehicle.id)
        this.props.onSelectedVehicleCdsUpdate(
            this.checkAllSelected() ?
                this.removeVehicles(selectedVehicleCds, filteredVehicleCds) :
                this.addVehicles(selectedVehicleCds, filteredVehicleCds)
        )
    }

    checkAllSelected = () => (
        this.props.vehicles.every(vehicle => this.props.selectedVehicleCds.includes(vehicle.id))
    )

    getHeadProps = name => {
        const noTooltipsColumns = ['level', 'selectAll']

        let clickHandler
        if (name === 'selectAll') clickHandler = this.handleSelectAllClick
        else if (name !== 'level') clickHandler = () => this.handleHeaderClick(name)

        return {
            className: classNames(
                styles.th,
                {
                    [styles.th__isSortable]: name !== 'level',
                    [styles[`th__${this.props.sortOrder}`]]: this.props.sortColumn === name,
                    [styles.th__basis]: name === 'name',
                }
            ),
            onClick: clickHandler,
            'data-tip': !noTooltipsColumns.includes(name),
            'data-for': noTooltipsColumns.includes(name) ? null : HEADER_TOOLTIPS[name].id,
        }
    }

    renderTableRow(vehicle) {
        const isSelected = this.props.selectedVehicleCds.includes(vehicle.id)
        const onClick = (event) => {
            if (event.target.type === 'checkbox') return false
            this.toggleVehicleSelected(vehicle.id)
        }

        return (
            <TableRow
                key={vehicle.techName}
                isHoverable
                isClickable
                isActive={isSelected}
                onClick={onClick}
            >
                <div className={styles.td}>
                    <Checkbox
                        onChange={onClick}
                        isChecked={isSelected}
                    />
                </div>
                <div className={styles.td}>
                    <div className={styles.icon}>
                        <Nation nation={vehicle.nation} realm={this.props.realm} />
                    </div>
                </div>
                <div className={styles.td}>
                    <div className={styles.icon}>
                        <VehicleType type={vehicle.type} mini />
                    </div>
                </div>
                <div className={styles.td}>
                    <div className={styles.icon}>
                        <span className={styles.level}>{toRoman(vehicle.level)}</span>
                    </div>
                </div>
                <div className={classNames(styles.td, styles.td__basis)}>
                    <img
                        className={styles.vehicleImage}
                        src={vehicle.smallIconUrl}
                    />
                    <p className={styles.vehicleTitle}>
                        {vehicle.shortName}
                    </p>
                    {vehicle.readiness && !vehicle.readiness.isReady && (
                        <span
                            className={styles.warningIcon}
                            data-tip data-for={VEHICLE_NOT_READY_TOOLTIP.id}
                        >
                        </span>
                    )}
                </div>
            </TableRow>
        )
    }

    renderList() {
        const tableRows = this.props.vehicles.map(this.renderTableRow)

        return (
            <div className={styles.list}>
                <ScrollArea
                    ref={component => this._scrollArea = component}
                    stepHeight={TABLE_ROW_HEIGHT}
                    scrollableAreaHeight={TABLE_HEIGHT}
                    modifier='table'
                >
                    <TableBody>
                        {tableRows}
                    </TableBody>
                </ScrollArea>
            </div>
        )
    }

    renderEmptyList() {
        return (
            <div className={styles.emptyList}>
                <p className={styles.message}>{NOT_FOUND_MESSAGE}</p>
            </div>
        )
    }

    render() {
        const allChecked = this.checkAllSelected()
        return (
            <div className={styles.base}>
                <Table>
                    <TableHead>
                        <div {...this.getHeadProps('selectAll')}>
                            <span className={classNames(styles.thCondition, styles.thCondition__hover)} />
                            <span className={classNames(styles.thCondition, styles.thCondition__active)} />
                            <div className={classNames(styles.thKey, styles.thKey__inactive)}>
                                <VehicleFilterCheckbox isChecked={allChecked} onChange={this.handleSelectAllClick} />
                            </div>
                        </div>
                        <div {...this.getHeadProps('nation')}>
                            <span className={classNames(styles.thCondition, styles.thCondition__hover)} />
                            <span className={classNames(styles.thCondition, styles.thCondition__active)} />
                            <div className={styles.thKey}>
                                <div className={styles.icon}>
                                    <Nation nation='all_nations' />
                                </div>
                            </div>
                            <DefaultTooltip {...HEADER_TOOLTIPS['nation']} />
                        </div>
                        <div {...this.getHeadProps('type')}>
                            <span className={classNames(styles.thCondition, styles.thCondition__hover)} />
                            <span className={classNames(styles.thCondition, styles.thCondition__active)} />
                            <div className={styles.thKey}>
                                <div className={styles.icon}>
                                    <span className={styles.iconVehicleTypes} />
                                </div>
                            </div>
                            <DefaultTooltip {...HEADER_TOOLTIPS['type']} />
                        </div>
                        <div {...this.getHeadProps('level')}>
                            <div className={styles.thKey}>
                                <div className={styles.icon}>
                                    <span className={styles.iconLevels} />
                                </div>
                            </div>
                        </div>
                        <div {...this.getHeadProps('name')}>
                            <span className={classNames(styles.thCondition, styles.thCondition__hover)} />
                            <span className={classNames(styles.thCondition, styles.thCondition__active)} />
                            <div className={styles.thKey}>
                                {NAME_COLUMN_TITLE}
                            </div>
                            <DefaultTooltip {...HEADER_TOOLTIPS['name']} />
                        </div>
                    </TableHead>
                    {this.props.vehicles.length ? this.renderList() : this.renderEmptyList()}
                </Table>
                <DefaultTooltip {...VEHICLE_NOT_READY_TOOLTIP} />
            </div>
        )
    }
}

export default VehiclesTable



// WEBPACK FOOTER //
// ./components/VehiclesTable/VehiclesTable.jsx